import React from 'react';
import { Link } from 'wouter';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumb Navigation with Schema.org BreadcrumbList markup
 *
 * Features:
 * - Schema.org BreadcrumbList structured data
 * - Responsive design
 * - Home icon for first item
 * - SEO-optimized
 */
const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, className = '' }) => {
  // Add home as first item if not already present
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', href: '/' },
    ...items
  ];

  // Generate Schema.org BreadcrumbList structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `https://yade-home.de${item.href}`
    }))
  };

  return (
    <>
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* Breadcrumb Navigation */}
      <nav
        aria-label="Breadcrumb"
        className={`bg-gray-50 border-b border-gray-200 ${className}`}
      >
        <div className="container mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              const isFirst = index === 0;

              return (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                  )}

                  {isLast ? (
                    <span
                      className="text-gray-700 font-medium flex items-center"
                      aria-current="page"
                    >
                      {isFirst && <Home className="h-4 w-4 mr-1" />}
                      {item.label}
                    </span>
                  ) : (
                    <Link href={item.href}>
                      <a className="text-gray-500 hover:text-gray-900 transition-colors flex items-center">
                        {isFirst && <Home className="h-4 w-4 mr-1" />}
                        {item.label}
                      </a>
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumb;
